import { Injectable, ErrorHandler } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserInfo } from '../../shared/models/user-info.model';
import { BusinessHours } from '../models/business-hours.model';
import { AuthEventType, IAuthEvent } from '../../auth/models/auth-event.model';
import { AuthService } from '../../auth/auth/auth.service';
import { UserService } from '../../shared/user/user.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class BusinessHoursService {

  userInfo: UserInfo;

  constructor(private _http: HttpClient,
    private _auth: AuthService,
    private _userService: UserService,
    private _errorHandler: ErrorHandler, ) {
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));
  }

  private async _onAuthEvent(e: IAuthEvent): Promise<void> {
    if (!e) {
      return;
    }
    switch (e.type) {
      case AuthEventType.SignIn:
        this.userInfo = await this._userService.getUserInfo();
        break;
      case AuthEventType.InvalidSignIn:
      case AuthEventType.SignOut:
        this.userInfo = null;
        break;
    }
  }

  /**
  * Method creates a new BusinessHour in the API Server via a post request with the business id and a business hours object.
  * @param businessId {number} used to identify the business
  * @param hours {BusinessHours} business hours object
  * @returns {Promise<BusinessHours>} API Server will respond with a new Business OBJ.
  */
  async createBusinessHours(businessId: number, hours: BusinessHours): Promise<BusinessHours> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/hours/`;
    try {
      const RESPONSE = await this._http.post<BusinessHours>(URL, hours).toPromise();
      const BUSINESS_HOURS = BusinessHours.parse(RESPONSE);
      if (!BUSINESS_HOURS) {
        const ERROR = new Error('API did not return valid business hours object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_HOURS;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
  * Sends put request to api server to update a given businessHours.
  * Method updates the API Server with data from the json object with one field of note being passed in.
  * @param businessId {number} businessId
  * @param businessHourId {number} the id of the businessHours that will be updated
  * @param hours {BusinessHours} content to be updated.
  * @returns {Promise<BusinessHours>} API Server will respond with updated Business OBJ.
  */
  async updateBusinessHours(businessId: number, businessHourId: number, hours: BusinessHours): Promise<BusinessHours> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/hours/${businessHourId}`;
    try {
      const RESPONSE = await this._http.put<BusinessHours>(URL, hours).toPromise();
      const BUSINESS_HOURS = BusinessHours.parse(RESPONSE);
      if (!BUSINESS_HOURS) {
        const ERROR = new Error('API did not return valid business hours object');
        this._errorHandler.handleError(ERROR);
        throw ERROR;
      } else {
        return BUSINESS_HOURS;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
  * Method to call API to delete a business hours from a business
  * Requires that user is a flight deck admin to delete notes
  * @param businessId {number} the business to remove the admin from.
  * @param businessHourId {number} the specific business hour to be deleted.
  * @returns {Promise<string>} Returns SUCCESS or error message for display to user.
  */
  async deleteBusinessHours(businessId: number, businessHourId: number): Promise<boolean> {
    if (!this._auth.authenticated || !this._auth.token || !this._auth.endUser) {
      const ERROR = new Error('User not authorized');
      this._errorHandler.handleError(ERROR);
      throw ERROR;
    }
    const URL = `${environment.airportsApiUrl}/business/${businessId}/hours/${businessHourId}`;
    try {
      const RESPONSE = await this._http.delete(URL).toPromise();
      return true;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw false;
    }
  }
}
